import { Components, PaletteMode, Theme } from '@mui/material';
import { themePalette } from '../theme.palette';
import dimensions from '../dimensions';
import { typographyTokens } from '../typography-tokens';

// Extend MUI's Alert module to support custom variants
declare module '@mui/material/Alert' {
  interface AlertPropsVariantOverrides {
    'success-solid': true;
    'warning-solid': true;
    'error-solid': true;
    'info-solid': true;
    'suggest-solid': true;
    'default-solid': true;
    'success-transparent': true;
    'warning-transparent': true;
    'error-transparent': true;
    'info-transparent': true;
    'suggest-transparent': true;
    'default-transparent': true;
    // Disable default MUI variants
    filled: false;
    outlined: false;
    standard: false;
  }
}

export const MuiAlertTheme = (mode: PaletteMode): Components<Theme>['MuiAlert'] => {
  const { componentTokens, tokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    styleOverrides: {
      root: {
        padding: `${spacing(4)}px ${spacing(5)}px`,
        alignItems: 'center',
        borderRadius: spacing(1.5), // 6px
        fontSize: typographyTokens.fontSizes.xs,
        fontWeight: typographyTokens.fontWeights.bold,
        lineHeight: typographyTokens.lineHeights.xs,
        backdropFilter: 'blur(50px)',
        border: '1px solid',
        '.MuiTypography-root': {
          lineHeight: 'normal',
        },
      },
      icon: {
        padding: 0,
        marginRight: spacing(5),
        opacity: 1,
        fontSize: spacing(6),
      },
      message: {
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        fontSize: typographyTokens.fontSizes.xs,
        lineHeight: typographyTokens.lineHeights.xs,
        fontWeight: typographyTokens.fontWeights.regular,
        color: componentTokens.notifications.colors.text.normal.resting,
        flexGrow: 1,
        '& .MuiAlertTitle-root': {
          margin: 0,
          marginTop: 0,
          marginBottom: 0,
          fontWeight: typographyTokens.fontWeights.semibold,
          lineHeight: typographyTokens.lineHeights.sm,
          fontSize: typographyTokens.fontSizes.sm,
          '& *': {
            margin: 0,
            marginTop: 0,
            marginBottom: 0,
            fontWeight: typographyTokens.fontWeights.semibold,
            lineHeight: typographyTokens.lineHeights.sm,
            fontSize: typographyTokens.fontSizes.sm,
          },
        },
        '> .MuiBox-root': {
          width: '100%',
        },
      },
      action: {
        padding: 0,
        marginLeft: spacing(2), // 8px
        marginRight: 0,
        paddingLeft: 0,
        alignSelf: 'flex-start',
        // Style the close IconButton to match original pounce design
        '& .MuiIconButton-root': {
          color: 'inherit', // Match the alert text color
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: '50%', // circle shape
          '&:hover': {
            backgroundColor: tokens.stroke.neutral[5],
            borderColor: 'transparent',
          },
        },
      },
    },
    variants: [
      // Solid variants
      {
        props: { variant: 'success-solid' },
        style: {
          backgroundColor: componentTokens.notifications.colors.background.success.hover,
          borderColor: componentTokens.notifications.colors.stroke.success.hover,
          color: componentTokens.notifications.colors.text.title.resting,
          '& .MuiAlert-icon .MuiSvgIcon-root': {
            color: componentTokens.notifications.colors.assets.success.resting,
          },
        },
      },
      {
        props: { variant: 'warning-solid' },
        style: {
          backgroundColor: componentTokens.notifications.colors.background.warning.hover,
          borderColor: componentTokens.notifications.colors.stroke.warning.hover,
          color: componentTokens.notifications.colors.text.title.resting,
          '& .MuiAlert-icon': {
            color: componentTokens.notifications.colors.assets.warning.static,
          },
        },
      },
      {
        props: { variant: 'error-solid' },
        style: {
          backgroundColor: componentTokens.notifications.colors.background.error.hover,
          borderColor: componentTokens.notifications.colors.stroke.error.hover,
          color: componentTokens.notifications.colors.text.title.resting,
          '& .MuiAlert-icon': {
            '& .MuiSvgIcon-root': {
              color: componentTokens.notifications.colors.assets.destructive.resting,
            },
          },
        },
      },
      {
        props: { variant: 'info-solid' },
        style: {
          backgroundColor: componentTokens.notifications.colors.background.info.hover,
          borderColor: componentTokens.notifications.colors.stroke.info.hover,
          color: componentTokens.notifications.colors.text.title.resting,
          '& .MuiAlert-root': {
            '&. MuiSvgIcon-root': {
              color: componentTokens.notifications.colors.assets.info.resting,
            },
          },
        },
      },
      {
        props: { variant: 'suggest-solid' },
        style: {
          backgroundColor: componentTokens.notifications.colors.background.suggest.hover,
          borderColor: componentTokens.notifications.colors.stroke.suggest.hover,
          color: componentTokens.notifications.colors.text.title.resting,
          '& .MuiAlert-root': {
            '&. MuiSvgIcon-root': {
              color: componentTokens.notifications.colors.assets.suggest.static,
            },
          },
        },
      },
      {
        props: { variant: 'default-solid' },
        style: {
          backgroundColor: componentTokens.notifications.colors.background.neutral.hover,
          borderColor: componentTokens.notifications.colors.stroke.neutral.hover,
          color: componentTokens.notifications.colors.text.title.resting,
          '& .MuiAlert-root': {
            '&. MuiSvgIcon-root': {
              color: componentTokens.notifications.colors.assets.neutral.static,
            },
          },
        },
      },
      // Transparent variants
      {
        props: { variant: 'success-transparent' },
        style: {
          backgroundColor: componentTokens.notifications.colors.background.success.resting,
          borderColor: componentTokens.notifications.colors.stroke.success.resting,
          color: componentTokens.notifications.colors.text.title.resting,
          '& .MuiAlert-icon': {
            color: componentTokens.notifications.colors.assets.success.resting,
          },
        },
      },
      {
        props: { variant: 'warning-transparent' },
        style: {
          backgroundColor: componentTokens.notifications.colors.background.warning.resting,
          borderColor: componentTokens.notifications.colors.stroke.warning.resting,
          color: componentTokens.notifications.colors.text.title.resting,
          '& .MuiAlert-icon': {
            color: componentTokens.notifications.colors.assets.warning.static,
          },
        },
      },
      {
        props: { variant: 'error-transparent' },
        style: {
          backgroundColor: componentTokens.notifications.colors.background.error.resting,
          borderColor: componentTokens.notifications.colors.stroke.error.resting,
          color: componentTokens.notifications.colors.text.title.resting,
          '& .MuiAlert-icon': {
            color: componentTokens.notifications.colors.assets.destructive.resting,
          },
        },
      },
      {
        props: { variant: 'info-transparent' },
        style: {
          backgroundColor: componentTokens.notifications.colors.background.info.resting,
          borderColor: componentTokens.notifications.colors.stroke.info.resting,
          color: componentTokens.notifications.colors.text.title.resting,
          '& .MuiAlert-icon': {
            color: componentTokens.notifications.colors.assets.info.resting,
          },
        },
      },
      {
        props: { variant: 'suggest-transparent' },
        style: {
          backgroundColor: componentTokens.notifications.colors.background.suggest.resting,
          borderColor: componentTokens.notifications.colors.stroke.suggest.resting,
          color: componentTokens.notifications.colors.text.title.resting,
          '& .MuiAlert-icon': {
            color: componentTokens.notifications.colors.assets.suggest.static,
          },
        },
      },
      {
        props: { variant: 'default-transparent' },
        style: {
          backgroundColor: componentTokens.notifications.colors.background.neutral.resting,
          borderColor: componentTokens.notifications.colors.stroke.neutral.resting,
          color: componentTokens.notifications.colors.text.title.resting,
          '& .MuiAlert-icon': {
            color: componentTokens.notifications.colors.assets.neutral.static,
          },
        },
      },
    ],
  };
};
